import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { _updateInvitation, _updateMember } from '@/api/entity/mutations';
import { ENTITY_QUERY_KEYS } from '@/api/entity/queries';
import { EntityRole, EntityTeamLegalRole } from '@/api/entity/types';
import { _getEntityRoles } from '@/api/entity-roles/queries';
import { ENTITY_VIEWS_QUERY_KEYS } from '@/api/entity-views/queries';
import { useCreateRoleRequest } from '@/api/role-requests/mutations';
import { RoleRequestStatus, RoleRequestType } from '@/api/role-requests/types';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { entityRoleList, isDirectorList } from '@/pages/manage-team/common';
import { Drawer, Input, Select } from '@/shared/design-system';
import { Permission, RequirePermissionInline, useCan } from '@/shared/modules/permissions';
import { useEntitySlice } from '@/stores/selectors';

import { DIRECTOR_CHANGE_TOOLTIP } from './team-member-copy';
import { TeamMemberFields } from './team-member-fields.styled';

export const ROLE_ACCESS_DENIED_MESSAGE =
  "You don't have permission to view this entity's roles, so the role cannot be changed here (EntityViewRoles).";

export interface TeamMemberEditData {
  email: string;
  role: EntityRole | '';
  legalRole: EntityTeamLegalRole | '';
  memberId: string | null;
  userId: string | null;
  invitationId: string | null;
  entityId?: string | null;
}

const initialState: Pick<TeamMemberEditData, 'legalRole' | 'role'> = { legalRole: '', role: '' };

export const TeamMemberEdit = () => {
  const { open, handleClose, props } = useGlobalModal('TEAM_MEMBER_EDIT_MODAL');
  const queryClient = useQueryClient();
  const [teamMember, setTeamMember] = useState(initialState);
  const { stepForms } = useEntitySlice();
  const entityId = props?.entityId || stepForms.id;

  const { mutateAsync: updateMember, isPending: isUpdatingMember } = useMutation({
    mutationFn: _updateMember,
  });
  const { mutateAsync: updateInvitation, isPending: isUpdatingInvitation } = useMutation({
    mutationFn: _updateInvitation,
  });
  const { mutateAsync: requestRoleChange, isPending: isRequestingRole } = useCreateRoleRequest({
    entityId,
  });
  const canViewRoles = useCan(Permission.EntityViewRoles);
  const canAssignRoles = useCan(Permission.EntityAssignRoles);
  const canApproveRoles = useCan(Permission.EntityApproveRoleRequests);
  const canManageTeam = useCan(Permission.EntityManageTeam);
  const roles = useQuery({
    ..._getEntityRoles(entityId),
    enabled: open && !!entityId && canViewRoles,
  });

  const roleChanged = props?.role !== teamMember.role;
  const legalRoleChanged = props?.legalRole !== teamMember.legalRole;
  const canChangeRole =
    canViewRoles && (props?.memberId ? canAssignRoles : canManageTeam && canApproveRoles);
  const selectedRoleId = roles.data?.roles.find((role) => role.systemKey === teamMember.role)?.id;
  const isSaving = isUpdatingMember || isUpdatingInvitation || isRequestingRole;
  const canSave =
    !!entityId &&
    !!(props?.memberId || props?.invitationId) &&
    (roleChanged || legalRoleChanged) &&
    (!roleChanged || (canChangeRole && (!props?.memberId || !!(props.userId && selectedRoleId)))) &&
    (!legalRoleChanged || canManageTeam) &&
    !isSaving;
  const roleDeniedMessage = props?.memberId
    ? "You do not have permission to change this member's role (EntityAssignRoles)."
    : "You do not have permission to change this invitation's role (EntityManageTeam and EntityApproveRoleRequests).";

  useEffect(() => {
    if (props) {
      setTeamMember({
        legalRole: props.legalRole,
        role: props.role,
      });
    }
  }, [props]);

  const handleEditTeamMember = async () => {
    if (!canSave || !props) return;

    const onSuccess = () => {
      handleCloseClick();
      setTeamMember(initialState);
      queryClient.invalidateQueries({
        queryKey: [ENTITY_QUERY_KEYS.entities, entityId],
      });
      queryClient.invalidateQueries({
        queryKey: [ENTITY_VIEWS_QUERY_KEYS.teamAssociates, entityId],
      });
    };

    const updateBody = {
      entityId,
      role: teamMember.role as EntityRole,
      legalRole: teamMember.legalRole as EntityTeamLegalRole,
      title: 'Team Member',
    };

    if (props?.memberId) {
      let appliedRole = props.role;

      if (roleChanged) {
        if (!props.userId || !selectedRoleId) return;

        const result = await requestRoleChange({
          type: RoleRequestType.AssignRole,
          payload: { roleId: selectedRoleId, userId: props.userId },
        });
        if (result.status === RoleRequestStatus.Approved) appliedRole = teamMember.role;
      }

      if (legalRoleChanged) {
        await updateMember({
          ...updateBody,
          role: appliedRole as EntityRole,
          memberId: props.memberId,
        });
      }

      onSuccess();
    } else if (props?.invitationId) {
      await updateInvitation({
        ...updateBody,
        invitationId: props.invitationId,
      });
      onSuccess();
    }
  };

  const handleCloseClick = () => {
    setTeamMember(initialState);
    handleClose();
  };

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => !next && handleCloseClick()}
      title="Edit Team Member"
      showClose
      secondaryAction={{ children: 'Cancel', onClick: handleCloseClick }}
      primaryAction={{
        children: 'Save',
        onClick: () => void handleEditTeamMember().catch(() => undefined),
        disabled: !canSave,
      }}
    >
      <TeamMemberFields>
        <Input size="lg" label="Email" required value={props?.email || ''} disabled />

        <RequirePermissionInline
          permission={Permission.EntityViewRoles}
          deniedMessage={ROLE_ACCESS_DENIED_MESSAGE}
        >
          <Select
            size="lg"
            label="Role"
            required
            placeholder="Select role"
            options={entityRoleList}
            value={teamMember.role || null}
            disabled={!canChangeRole || isSaving}
            helperText={!canChangeRole ? roleDeniedMessage : undefined}
            onChange={(role) => setTeamMember((prev) => ({ ...prev, role: role as EntityRole }))}
          />
        </RequirePermissionInline>

        <Select
          size="lg"
          label="Director"
          required
          tooltip={DIRECTOR_CHANGE_TOOLTIP}
          placeholder="Select is this user a Director"
          options={isDirectorList}
          value={teamMember.legalRole || null}
          disabled={!canManageTeam || isSaving}
          helperText={
            !canManageTeam
              ? 'You do not have permission to change Director status (EntityManageTeam).'
              : undefined
          }
          onChange={(legalRole) =>
            setTeamMember((prev) => ({ ...prev, legalRole: legalRole as EntityTeamLegalRole }))
          }
        />
      </TeamMemberFields>
    </Drawer>
  );
};
